<template>
  <div class="w-full flex flex-col justify-center px-12 py-16 relative">
    <!-- Language -->
    <div
      :class="[
        'absolute top-4 z-30 flex gap-4',
        locale === 'ar' ? 'right-4 flex-row-reverse' : 'left-4 flex-row',
      ]"
    >
      <img
        src="@/assets/icons/language.svg"
        alt="Language"
        class="w-6 h-6 cursor-pointer"
        @click="toggleLang"
      />
    </div>

    <p
      class="text-[30px] font-semibold font-[Inter] text-[var(--primary)] text-center mb-4"
    >
      {{ $t("login.loginForm.login_title") }}
    </p>

    <form @submit.prevent="handleLogin" class="space-y-4">
      <!-- Email Field -->
      <div>
        <div
          class="flex items-center bg-[var(--surface-2)] text-[var(--on-surface)] px-4 py-2 w-full mb-4 rounded relative"
        >
          <template v-if="locale === 'ar'">
            <input
              v-model="email"
              type="email"
              :placeholder="$t('login.loginForm.email')"
              class="bg-transparent w-full focus:outline-none placeholder-[var(--muted)] text-right pr-10"
              required
              dir="rtl"
            />
            <img
              src="@/assets/icons/email.svg"
              alt="Email"
              class="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2"
            />
          </template>
          <template v-else>
            <input
              v-model="email"
              type="email"
              :placeholder="$t('login.loginForm.email')"
              class="bg-transparent w-full focus:outline-none placeholder-[var(--muted)] text-left pl-10"
              required
              dir="ltr"
            />
            <img
              src="@/assets/icons/email.svg"
              alt="Email"
              class="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2"
            />
          </template>
        </div>
      </div>

      <!-- Password Field -->
      <div>
        <div
          class="flex items-center bg-[var(--surface-2)] px-4 py-2 w-full rounded relative"
        >
          <template v-if="locale === 'ar'">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('login.loginForm.password')"
              class="bg-transparent w-full focus:outline-none placeholder-[var(--muted)] text-[var(--on-surface)] text-right pr-10"
              required
              dir="rtl"
            />
            <img
              src="@/assets/icons/password.svg"
              alt="Password"
              class="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2"
            />
          </template>
          <template v-else>
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('login.loginForm.password')"
              class="bg-transparent w-full focus:outline-none placeholder-[var(--muted)] text-[var(--on-surface)] text-left pl-10"
              required
              dir="ltr"
            />
            <img
              src="@/assets/icons/password.svg"
              alt="Password"
              class="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2"
            />
          </template>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        class="p-3 bg-[color-mix(in_srgb,var(--danger)_20%,transparent)] border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] rounded-lg text-center"
      >
        <div class="flex items-center justify-center gap-2">
          <svg
            class="w-4 h-4"
            :style="{ color: 'var(--danger)' }"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <span class="text-sm font-medium text-[var(--danger)]">{{
            errorMessage
          }}</span>
        </div>
      </div>

      <!-- Forgot password -->
      <div class="flex justify-between items-center text-sm">
        <span></span>
        <button
          type="button"
          @click="showForgotPassword = true"
          class="text-[var(--muted)] hover:underline focus:outline-none"
        >
          {{ $t("login.loginForm.forgot_password") }}
        </button>
      </div>

      <!-- Submit Button -->
      <button
        type="submit"
        class="w-full bg-[var(--login-btn-bg)] text-[var(--login-btn-text)] font-semibold py-2 rounded-none transition-all duration-300 hover:shadow-[var(--glow-login)] hover:[filter:var(--login-drop-glow)]"
      >
        {{ $t("login.loginForm.login_button") }}
      </button>
    </form>

    <!-- Google -->
    <div class="flex justify-center mb-4 mt-6">
      <button
        @click="loginWithGoogle"
        class="w-full flex items-center justify-center gap-2 py-2
         rounded border
         bg-[var(--google-btn-bg)]
         border-[var(--google-btn-border)]
         text-[var(--google-btn-text)]
         transition-all duration-300
         hover:shadow-[var(--glow-google)]
         hover:[filter:var(--google-drop-glow)]"
      >
        <img src="@/assets/icons/google.svg" alt="Google" class="w-5 h-5" />
        <span class="text-sm font-medium">Google</span>
      </button>
    </div>

    <!-- Sign up prompt -->
    <div class="text-center text-[var(--muted)]">
      {{ $t("login.loginForm.no_account") }}
      <RouterLink
        to="/signup"
        class="text-[var(--primary)] font-semibold hover:underline"
      >
        {{ $t("login.loginForm.sign_up") }}
      </RouterLink>
    </div>

    <!-- Forgot Password Modal -->
    <div
      v-if="showForgotPassword"
      class="fixed inset-0 bg-[var(--overlay-scrim)] flex items-center justify-center z-50"
    >
      <div
        class="bg-[var(--surface)] rounded-lg p-6 w-full max-w-md mx-4 border border-[var(--border)]"
      >
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-semibold text-[var(--on-surface)]">
            Reset Password
          </h3>
          <button
            @click="showForgotPassword = false"
            class="text-[var(--muted)] hover:text-[var(--on-surface)] text-2xl focus:outline-none"
          >
            ×
          </button>
        </div>

        <p class="text-[var(--muted)] mb-4">
          Enter your email address and we'll send you a link to reset your
          password.
        </p>

        <form @submit.prevent="handleForgotPassword" class="space-y-4">
          <div
            class="bg-[var(--surface-2)] px-3 py-2 rounded relative flex items-center"
          >
            <input
              v-model="resetEmail"
              type="email"
              placeholder="Enter your email"
              class="w-full bg-transparent text-[var(--on-surface)] placeholder-[var(--muted)] focus:outline-none pl-10"
              required
            />
            <img
              src="@/assets/icons/email.svg"
              alt="Email"
              class="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2"
            />
          </div>

          <!-- Success -->
          <div
            v-if="resetSuccessMessage"
            class="p-3 bg-[color-mix(in_srgb,var(--success)_18%,transparent)] border border-[color-mix(in_srgb,var(--success)_30%,transparent)] rounded-lg text-center"
          >
            <div class="flex items-center justify-center gap-2">
              <svg
                class="w-4 h-4"
                :style="{ color: 'var(--success)' }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="text-sm font-medium"
                :style="{ color: 'var(--success)' }"
                >{{ resetSuccessMessage }}</span
              >
            </div>
          </div>

          <!-- Error -->
          <div
            v-if="resetErrorMessage"
            class="p-3 bg-[color-mix(in_srgb,var(--danger)_20%,transparent)] border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] rounded-lg text-center"
          >
            <div class="flex items-center justify-center gap-2">
              <svg
                class="w-4 h-4"
                :style="{ color: 'var(--danger)' }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span class="text-sm font-medium text-[var(--danger)]">{{
                resetErrorMessage
              }}</span>
            </div>
          </div>

          <div class="flex gap-3">
            <button
              type="button"
              @click="showForgotPassword = false"
              class="flex-1 py-2 px-4 border border-[var(--border)] text-[var(--on-surface)] rounded hover:bg-[var(--surface-2)] transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="isResetting"
              class="flex-1 py-2 px-4 bg-[var(--primary)] text-[var(--on-primary)] font-semibold rounded hover:bg-[var(--primary-200)] transition disabled:opacity-50"
            >
              {{ isResetting ? "Sending..." : "Send Reset Link" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Autofill + inputs themed */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px var(--surface-2) inset !important;
  -webkit-text-fill-color: var(--on-surface) !important;
  transition: background-color 5000s ease-in-out 0s;
}
input:-moz-autofill {
  background-color: var(--surface-2) !important;
  color: var(--on-surface) !important;
}
input:-ms-input-placeholder {
  color: var(--muted) !important;
}
input::-webkit-calendar-picker-indicator {
  display: none !important;
}
input[autocomplete] {
  background-color: transparent !important;
}
input:focus {
  background-color: transparent !important;
  outline: none !important;
}
input:not(:focus) {
  background-color: transparent !important;
}
</style>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

const router = useRouter();
const route = useRoute();
const { locale } = useI18n();

// Get redirect parameter from URL
const redirectPath = ref(null);

onMounted(() => {
  // Check if there's a redirect parameter in the URL
  const redirect = route.query.redirect;
  if (redirect) {
    redirectPath.value = redirect;
  }
});

function toggleLang() {
  locale.value = locale.value === "en" ? "ar" : "en";
  localStorage.setItem("lang", locale.value);
  document.documentElement.setAttribute(
    "dir",
    locale.value === "ar" ? "rtl" : "ltr"
  );
}

const email = ref("");
const password = ref("");
const errorMessage = ref("");
const showPassword = ref(false);

// Forgot password variables
const showForgotPassword = ref(false);
const resetEmail = ref("");
const resetSuccessMessage = ref("");
const resetErrorMessage = ref("");
const isResetting = ref(false);

import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "@/firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";

async function handleLogin() {
  // Clear any previous error messages
  errorMessage.value = "";

  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email.value,
      password.value
    );
    const user = userCredential.user;

    if (!user.emailVerified) {
      errorMessage.value = "Please verify your email before logging in.";
      // Give user time to read the error message before redirecting
      setTimeout(() => {
        router.push("/verifyEmail");
      }, 3000); // 3 seconds delay
      return;
    }

    // Check if user is coming from checkout or booking duration
    const redirectToCheckout = localStorage.getItem("redirectToCheckout");
    const redirectToBookingDuration = localStorage.getItem(
      "redirectToBookingDuration"
    );

    if (redirectToCheckout === "true") {
      // Redirect back to checkout to continue order
      router.push("/checkout");
    } else if (redirectToBookingDuration === "true") {
      // Redirect to booking duration for in-house pre-order
      router.push("/bookingduration");
    } else if (redirectPath.value) {
      // Redirect to the specified path from URL parameter
      router.push(redirectPath.value);
    } else {
      // Default redirect to booking duration
      router.push("/bookingduration");
    }
  } catch (error) {
    // Handle specific Firebase auth errors
    switch (error.code) {
      case "auth/user-not-found":
        errorMessage.value =
          "Email not found. Please check your email address.";
        break;
      case "auth/wrong-password":
        errorMessage.value = "Incorrect password. Please try again.";
        break;
      case "auth/invalid-email":
        errorMessage.value =
          "Invalid email format. Please enter a valid email.";
        break;
      case "auth/too-many-requests":
        errorMessage.value =
          "Too many failed attempts. Please try again later.";
        break;
      case "auth/user-disabled":
        errorMessage.value =
          "This account has been disabled. Please contact support.";
        break;
      default:
        errorMessage.value = "Please check your credentials and try again.";
    }
  }
}

async function handleForgotPassword() {
  // Clear previous messages
  resetSuccessMessage.value = "";
  resetErrorMessage.value = "";
  isResetting.value = true;

  try {
    await sendPasswordResetEmail(auth, resetEmail.value);
    resetSuccessMessage.value = "Password reset link sent! Check your email.";

    // Clear the form
    resetEmail.value = "";

    // Close modal after 3 seconds
    setTimeout(() => {
      showForgotPassword.value = false;
      resetSuccessMessage.value = "";
    }, 3000);
  } catch (error) {
    // Handle specific Firebase auth errors
    switch (error.code) {
      case "auth/user-not-found":
        resetErrorMessage.value = "No account found with this email address.";
        break;
      case "auth/invalid-email":
        resetErrorMessage.value = "Please enter a valid email address.";
        break;
      case "auth/too-many-requests":
        resetErrorMessage.value = "Too many attempts. Please try again later.";
        break;
      default:
        resetErrorMessage.value =
          "Failed to send reset email. Please try again.";
    }
  } finally {
    isResetting.value = false;
  }
}

async function loginWithGoogle() {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Check if user already exists in Firestore
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);

    // If user doesn't exist in Firestore, save their data
    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        username: user.displayName || user.email?.split("@")[0] || "User",
        phone: user.phoneNumber || "",
        email: user.email,
        createdAt: new Date(),
      });
      console.log("New Google user data saved to Firestore");
    }

    // Optional: check if email is verified
    if (!user.emailVerified) {
      await user.sendEmailVerification();
    }

    // Check if user is coming from checkout or booking duration
    const redirectToCheckout = localStorage.getItem("redirectToCheckout");
    const redirectToBookingDuration = localStorage.getItem(
      "redirectToBookingDuration"
    );

    if (redirectToCheckout === "true") {
      // Redirect back to checkout to continue order
      router.push("/checkout");
    } else if (redirectToBookingDuration === "true") {
      // Redirect to booking duration for in-house pre-order
      router.push("/bookingduration");
    } else if (redirectPath.value) {
      // Redirect to the specified path from URL parameter
      router.push(redirectPath.value);
    } else {
      // Default redirect to booking duration
      router.push("/bookingduration");
    }
  } catch (error) {
    console.error("Google login error:", error.message);
    errorMessage.value = "Google login failed. Please try again.";
  }
}
</script>
